import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { guestEnter } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleEnter = async () => {
    setLoading(true);
    try {
      await guestEnter();
      toast.success('Welcome to AI Horse-Powered Systems!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Unable to enter. Please try again.');
      console.error('Guest enter error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <Link to="/" className="flex items-center justify-center gap-2 mb-12">
            <img src="/images/ai-horse-logo.png" alt="AI Horse-Powered" className="w-10 h-10 object-contain" />
            <span className="font-heading text-2xl font-bold text-white">AI Horse-Powered Systems</span>
          </Link>

          <h1 className="font-heading text-4xl font-bold text-white mb-3" data-testid="enter-heading">
            Welcome
          </h1>
          <p className="text-gray-400 mb-10 text-lg">
            Step inside your AI-powered command center
          </p>

          <Button 
            onClick={handleEnter}
            className="btn-primary w-full text-lg py-6"
            disabled={loading}
            data-testid="guest-enter-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>Enter <ArrowRight className="ml-2 w-5 h-5" /></>
            )}
          </Button>

          <p className="text-gray-500 text-sm mt-6">
            No sign-in required
          </p>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 border border-white/30 rounded-full" />
          <div className="absolute bottom-20 right-10 w-48 h-48 border border-white/30 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/20 rounded-full" />
        </div>
        
        <div className="text-center max-w-md relative z-10">
          <div className="mb-8">
            <img 
              src="/images/ai-horse-logo.png" 
              alt="AI Horse-Powered" 
              className="w-56 h-56 mx-auto mb-6 object-contain drop-shadow-[0_0_40px_rgba(34,211,238,0.3)]"
            />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-4">
            Your AI workforce awaits
          </h2>
          <p className="text-gray-400">
            Access 40+ virtual departments, track your orders, and chat with AI support — all in one place.
          </p>
        </div>
      </div>
    </div>
  );
}
